import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';

export interface CookieConsentPreferences {
    analytics: boolean;
    marketing: boolean;
}

interface StoredConsent extends CookieConsentPreferences {
    timestamp: number;
}

/** GDPR-style cookie consent persisted in localStorage. */
@Injectable({
    providedIn: 'root',
})
export class CookieConsentService {
    private static readonly CONSENT_KEY = 'saideline_cookie_consent';

    constructor() {
        if (environment.GOOGLE_CONSENT_MODE === 'advanced') {
            // Runs for every visitor, before any choice: consent stays "denied" until they accept.
            this.loadGoogleTag(this.getStoredConsent());
        }
        this.applyStoredConsent();
    }

    /** Whether the user has stored cookie preferences. */
    hasConsent(): boolean {
        return this.getStoredConsent() !== null;
    }

    /**
     * Persists consent choices and loads enabled third-party scripts.
     * @param preferences - Analytics and marketing opt-in flags
     */
    setConsent(preferences: CookieConsentPreferences): void {
        const consent: StoredConsent = {
            ...preferences,
            timestamp: Date.now(),
        };
        localStorage.setItem(CookieConsentService.CONSENT_KEY, JSON.stringify(consent));
        this.applyConsent(preferences);
    }

    private getStoredConsent(): StoredConsent | null {
        try {
            const raw = localStorage.getItem(CookieConsentService.CONSENT_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as StoredConsent;
        } catch {
            return null;
        }
    }

    private applyStoredConsent(): void {
        const consent = this.getStoredConsent();
        if (consent) {
            this.applyConsent(consent);
        }
    }

    private applyConsent(preferences: CookieConsentPreferences): void {
        if (preferences.analytics || preferences.marketing) {
            this.loadGoogleTag(preferences);
        } else {
            // Everything rejected: tell an already running tag (advanced mode, or a change of mind).
            this.updateGoogleConsent(preferences);
        }
        if (preferences.marketing) {
            this.loadMarketing();
        }
    }

    /**
     * Google Consent Mode v2 signals. Advertising signals (needed for Google Ads conversions)
     * follow the "Marketing" choice; analytics storage follows the "Analytics" choice.
     */
    private googleConsentState(preferences: CookieConsentPreferences): Record<string, 'granted' | 'denied'> {
        const ads = preferences.marketing ? 'granted' : 'denied';
        return {
            analytics_storage: preferences.analytics ? 'granted' : 'denied',
            ad_storage: ads,
            ad_user_data: ads,
            ad_personalization: ads,
        };
    }

    private updateGoogleConsent(preferences: CookieConsentPreferences): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const gtag = (window as any).gtag;
        if (typeof gtag === 'function') {
            gtag('consent', 'update', this.googleConsentState(preferences));
            // Without advertising consent, ad click identifiers are stripped from what Google receives.
            gtag('set', 'ads_data_redaction', !preferences.marketing);
        }
    }

    /**
     * Loads the Google tag once, with every consent signal denied by default, then applies the
     * visitor's choice if they have made one.
     *
     * @param preferences - Stored or just-made choice; null before the visitor has decided
     */
    private loadGoogleTag(preferences: CookieConsentPreferences | null): void {
        if (document.getElementById('gtag-script')) {
            // Already loaded: only the consent choice can have changed.
            if (preferences) this.updateGoogleConsent(preferences);
            return;
        }

        const measurementId = environment.GA_MEASUREMENT_ID;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        // gtag.js expects the `arguments` object itself on the data layer, not a plain array.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- the parameters only document the call signature
        function gtag(..._args: unknown[]): void {
            // eslint-disable-next-line prefer-rest-params
            w.dataLayer.push(arguments);
        }
        w.gtag = gtag;

        // Consent Mode v2: everything denied by default, then the visitor's actual choice.
        gtag('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
        });
        gtag('set', 'ads_data_redaction', !preferences?.marketing);
        // Without cookies, keep the ad click ID in links between pages so the conversion can still be attributed.
        gtag('set', 'url_passthrough', true);
        if (preferences) {
            gtag('consent', 'update', this.googleConsentState(preferences));
        }

        gtag('js', new Date());
        gtag('config', measurementId);

        const script = document.createElement('script');
        script.id = 'gtag-script';
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);
    }

    private loadMarketing(): void {
        if (document.getElementById('rewardful-script')) return;

        const inline = document.createElement('script');
        inline.textContent = `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`;
        document.head.appendChild(inline);

        const script = document.createElement('script');
        script.id = 'rewardful-script';
        script.async = true;
        script.src = 'https://r.wdfl.co/rw.js';
        script.setAttribute('data-rewardful', 'a2e480');
        document.head.appendChild(script);
    }
}
